import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { Box, Container, Flex, Grid } from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { PageHeading } from 'components';
import { useNavigate } from 'hooks';
import {
  annualDfiReportYearState,
  openReportModalState,
  openReportModalMobileState,
  reportStartDateState,
  reportEndDateState,
  reportFiltersState,
  reportState,
} from 'store';
import { Report } from 'types';
import { ReportCard, ReportModal, MobileReportFiltersModal } from './components';
import { useReports, reports as resportType } from './hooks';

export const Reports: React.FC = () => {
  const { t } = useTranslation('reports');

  const reports = useReports();
  const { navigateToReport } = useNavigate();
  const isMobile = useIsMobile();

  const [reportName, setReportName] = useState('');
  const setOpenReportModal = useSetRecoilState(openReportModalState);
  const setOpenReportMobileModal = useSetRecoilState(openReportModalMobileState);
  const setReport = useSetRecoilState(reportState);
  const resetDfiReportYear = useResetRecoilState(annualDfiReportYearState);
  const resetStartDate = useResetRecoilState(reportStartDateState);
  const resetEndDate = useResetRecoilState(reportEndDateState);
  const resetReportFilters = useResetRecoilState(reportFiltersState);

  const runReport = (report: Report) => {
    setReportName(report.name);
    resetDfiReportYear();
    resetStartDate();
    resetEndDate();
    resetReportFilters();
    setReport(report);
    if (isMobile) {
      setOpenReportMobileModal(true);
    } else if (report.hasDateFilter) {
      setOpenReportModal(true);
    } else {
      navigateToReport(report?.name);
    }
  };

  return (
    <Container px={{ base: 0, lg: 4, xl: 0 }} mb={6}>
      <Grid gridGap={2}>
        <PageHeading heading={t('reports_title', 'Consultee reports')} />
        <p>{t('reports_summary', 'Select a report you would like to configure and run')}</p>
        <Flex flexWrap='wrap' mt={6} gridGap={4}>
          {reports
            .filter((report) => !isMobile || report.visibleOnMobile)
            .map((report) => (
              <Box
                key={report.header}
                w={{
                  base: '100%',
                  lg: 'calc((100% - 32px) / 3)',
                  xl: 'calc((100% - 48px) / 4)',
                }}
              >
                <ReportCard report={report} runReport={() => runReport(report)} />
              </Box>
            ))}
        </Flex>
      </Grid>
      <ReportModal />
      <MobileReportFiltersModal reportName={reportName as resportType} />
    </Container>
  );
};
